import { useState, useEffect, useCallback } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

type Year = "all" | "2024" | "2025" | "2026";

interface Film {
  title: string;
  filename: string;
  year: "2024" | "2025" | "2026";
}

const BASE = "https://c3store.nyc3.cdn.digitaloceanspaces.com/mwff";

const FILMS: Film[] = [
  // 2024
  { title: "A Sleep Study", filename: "ASleepStudyPoster.jpeg", year: "2024" },
  {
    title: "Caffeine Freak",
    filename: "CaffeineFreakPoster.jpg",
    year: "2024",
  },
  { title: "Dark Roads", filename: "DarkRoadsPoster.jpg", year: "2024" },
  {
    title: "The Gourd The Brad & The Ghostly",
    filename: "GBGPoster.jpg",
    year: "2024",
  },
  { title: "Laid Bare", filename: "LaidBarePoster.jpg", year: "2024" },
  { title: "Munchies", filename: "MUNCHIESPoster.jpg", year: "2024" },
  { title: "Reclaim", filename: "ReclaimPoster.jpg", year: "2024" },
  { title: "Sugar Tits", filename: "SugarTitsPoster.jpg", year: "2024" },
  { title: "Turno De Noche", filename: "TurnoDeNochePoster.jpg", year: "2024" },
  {
    title: "Wellness Check",
    filename: "WellnessCheckPoster.png",
    year: "2024",
  },
  // 2025
  { title: "Agnes Wakes Up", filename: "AgnesWakesUpPoster.jpg", year: "2025" },
  { title: "Evil Dinner", filename: "EvilDinnerPoster.png", year: "2025" },
  { title: "Killergotchi", filename: "KillergotchiPoster.png", year: "2025" },
  { title: "Kllr 108.7", filename: "KllrPoster.jpg", year: "2025" },
  { title: "Looker", filename: "LookerPoster.png", year: "2025" },
  { title: "Salt Circles", filename: "SaltCirclesPoster.jpg", year: "2025" },
  { title: "Selfless", filename: "SelflessPoster.jpg", year: "2025" },
  {
    title: "The Ones Across the Pond",
    filename: "TheOnesAcrossthePondPoster.jpg",
    year: "2025",
  },
  { title: "The Solution", filename: "TheSolutionPoster.jpg", year: "2025" },
  { title: "Unknown User", filename: "UnknownUserPoster.jpg", year: "2025" },
  // 2026
  { title: "Afterimage", filename: "AfterImage.jpg", year: "2026" },
  { title: "Devourer", filename: "Devourer Poster.jpg", year: "2026" },
  {
    title: "Grave Danger",
    filename: "Grave Danger - Official Poster.jpg",
    year: "2026",
  },
  { title: "Hide", filename: "Hide.jpg", year: "2026" },
  {
    title: "Laundromat",
    filename: "LAUNDROMAT POSTER PORTRAIT.png",
    year: "2026",
  },
  { title: "Last Kill", filename: "Last Kill poster.jpg", year: "2026" },
  { title: "Long Play", filename: "LongPlay_Poster1.jpg", year: "2026" },
  { title: "Lupus", filename: "Lupus.jpg", year: "2026" },
  { title: "Someone\'s Here", filename: "Someone\'s Here.jpg", year: "2026" },
  {
    title: "Sous Vide (soo VEED)",
    filename: "Sous Vide Poster 2025.png",
    year: "2026",
  },
  { title: "TAPEHEAD", filename: "TAPEHEAD-poster.jpg", year: "2026" },
  {
    title: "The Screaming",
    filename: "TheScreamingPoster04_Vertical_V02.png",
    year: "2026",
  },
  { title: "Simon Says So", filename: "simonPOSTER.jpeg", year: "2026" },
];

export default function Selections() {
  const ref = useScrollAnimation();
  const [activeYear, setActiveYear] = useState<Year>("2026");
  const [selected, setSelected] = useState<Film | null>(null);
  const [page, setPage] = useState(0);

  const PAGE_SIZE = 14;

  const closeModal = useCallback(() => setSelected(null), []);

  // Close on Escape key
  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, closeModal]);

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  const visible =
    activeYear === "all" ? FILMS : FILMS.filter((f) => f.year === activeYear);
  const totalPages = Math.ceil(visible.length / PAGE_SIZE);
  const paginated = visible.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <section
      id="selections"
      style={{
        background: "#0a0a0f",
        padding: "clamp(64px,10vw,120px) clamp(20px,5vw,80px)",
      }}
    >
      <div ref={ref} style={{ maxWidth: "1280px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "48px" }}>
          <div
            className="section-label animate-on-scroll"
            style={{ marginBottom: "16px" }}
          >
            Official Lineup
          </div>
          <h2
            className="display-heading animate-on-scroll"
            style={{
              fontSize: "clamp(2.5rem,6vw,4.5rem)",
              color: "#e8e8e8",
              transitionDelay: "100ms",
            }}
          >
            Film <span style={{ color: "#cc0000" }}>Selections</span>
          </h2>
        </div>

        {/* Year filter */}
        <div
          className="animate-on-scroll"
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "40px",
            transitionDelay: "150ms",
          }}
        >
          {(["2026", "2025", "2024", "all"] as Year[]).map((yr) => (
            <button
              key={yr}
              onClick={() => { setActiveYear(yr); setPage(0); }}
              style={{
                padding: "8px 22px",
                borderRadius: "999px",
                border: `1px solid ${activeYear === yr ? "#cc0000" : "rgba(255,255,255,0.12)"}`,
                background:
                  activeYear === yr ? "rgba(204,0,0,0.15)" : "transparent",
                color: activeYear === yr ? "#e8e8e8" : "rgba(136,136,153,0.8)",
                fontFamily: '"Bebas Neue", cursive',
                fontSize: "0.85rem",
                letterSpacing: "0.15em",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {yr === "all" ? "All Years" : yr}
            </button>
          ))}
        </div>

        {/* Poster grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: "20px",
          }}
        >
          {paginated.map((film) => (
            <div
              key={film.filename}
              onClick={() => setSelected(film)}
              style={{
                aspectRatio: "2 / 3",
                borderRadius: "10px",
                overflow: "hidden",
                position: "relative",
                border: "1px solid rgba(255,255,255,0.06)",
                cursor: "pointer",
                transition: "transform 0.2s ease, border-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform =
                  "scale(1.03)";
                (e.currentTarget as HTMLDivElement).style.borderColor =
                  "rgba(204,0,0,0.4)";
                const overlay =
                  e.currentTarget.querySelector<HTMLDivElement>(
                    ".poster-overlay",
                  );
                if (overlay) overlay.style.opacity = "1";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform =
                  "scale(1)";
                (e.currentTarget as HTMLDivElement).style.borderColor =
                  "rgba(255,255,255,0.06)";
                const overlay =
                  e.currentTarget.querySelector<HTMLDivElement>(
                    ".poster-overlay",
                  );
                if (overlay) overlay.style.opacity = "0";
              }}
            >
              <img
                src={`${BASE}/${film.year}SelectionPosters/${encodeURIComponent(film.filename)}`}
                alt={film.title}
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <div
                className="poster-overlay"
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.85) 40%, transparent 100%)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  padding: "12px",
                  opacity: 0,
                  transition: "opacity 0.25s ease",
                }}
              >
                <span
                  style={{
                    fontFamily: '"Bebas Neue", cursive',
                    fontSize: "0.9rem",
                    letterSpacing: "0.08em",
                    color: "#e8e8e8",
                    lineHeight: 1.2,
                  }}
                >
                  {film.title}
                </span>
                <span
                  style={{
                    fontSize: "0.65rem",
                    letterSpacing: "0.18em",
                    color: "#cc0000",
                    fontFamily: '"Bebas Neue", cursive',
                    marginTop: "4px",
                  }}
                >
                  {film.year}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              marginTop: "40px",
            }}
          >
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 0}
              style={{
                padding: "8px 22px",
                borderRadius: "999px",
                border: `1px solid ${page === 0 ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.12)"}`,
                background: "transparent",
                color: page === 0 ? "rgba(136,136,153,0.3)" : "rgba(136,136,153,0.8)",
                fontFamily: '"Bebas Neue", cursive',
                fontSize: "0.85rem",
                letterSpacing: "0.15em",
                cursor: page === 0 ? "default" : "pointer",
                transition: "all 0.2s ease",
              }}
            >
              ← Prev
            </button>
            <span
              style={{
                fontFamily: '"Bebas Neue", cursive',
                fontSize: "0.85rem",
                letterSpacing: "0.15em",
                color: "rgba(136,136,153,0.8)",
              }}
            >
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page === totalPages - 1}
              style={{
                padding: "8px 22px",
                borderRadius: "999px",
                border: `1px solid ${page === totalPages - 1 ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.12)"}`,
                background: "transparent",
                color: page === totalPages - 1 ? "rgba(136,136,153,0.3)" : "rgba(136,136,153,0.8)",
                fontFamily: '"Bebas Neue", cursive',
                fontSize: "0.85rem",
                letterSpacing: "0.15em",
                cursor: page === totalPages - 1 ? "default" : "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {selected && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxHeight: "90vh",
              maxWidth: "min(480px, 90vw)",
              borderRadius: "14px",
              overflow: "hidden",
              boxShadow:
                "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(204,0,0,0.2)",
              animation: "scaleIn 0.22s ease",
            }}
          >
            <img
              src={`${BASE}/${selected.year}SelectionPosters/${selected.filename}`}
              alt={selected.title}
              style={{ display: "block", width: "100%", height: "auto" }}
            />

            {/* Close button */}
            <button
              onClick={closeModal}
              aria-label="Close"
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(0,0,0,0.6)",
                color: "#e8e8e8",
                fontSize: "1rem",
                lineHeight: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.93); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </section>
  );
}
